import {
  CognitoIdentityProviderClient,
  ConfirmSignUpCommand,
  SignUpCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { forEach, isEmpty, snakeCase } from 'lodash';
import { useParams } from 'react-router';
import { v4 as uuid } from 'uuid';

import useMixpanel from '../../hooks/useMixpanel';
import useStores from '../../hooks/useStores';
import { useLoginService } from '../loginService';
import { getPortalRuntimeConfig } from '../runtimeConfig';
import type {
  ICognitoSignupCardinals,
  ISignupCardinals,
  ISignupLinkAccount,
  ISignupLinkAccountWithToken,
} from './types';
import { signUpFlowStatEnum } from './types';

export const useSignUpService = () => {
  const { signUpStore } = useStores();
  const { state } = useLoginService();
  const { inviteKey } = useParams();
  const { awsRegion, cognito } = getPortalRuntimeConfig();
  const clientId = cognito.clientId;
  const client = new CognitoIdentityProviderClient({
    region: awsRegion,
  });
  const { track, trackFail, trackSuccess } = useMixpanel();

  const mutateSignup = useMutation({
    mutationFn: async (data: ISignupLinkAccount) => {
      try {
        await axios.post('/api/camel/auth/register_with_cognito/', data);
        signUpStore.setSignUpFlowState({
          ...signUpStore.signUpFlowState,
          state: signUpFlowStatEnum.welcome,
        });
      } catch (error) {
        signUpStore.setErrorMessager(error.message);
      }
    },
  });

  const mutateTokenSignup = useMutation({
    mutationFn: async (data: ISignupLinkAccountWithToken) => {
      try {
        await axios.post('/api/camel/auth/register_with_token/', data);
        signUpStore.setSignUpFlowState({
          ...signUpStore.signUpFlowState,
          state: signUpFlowStatEnum.welcome,
        });
      } catch (error) {
        signUpStore.setErrorMessager(error.message);
      }
    },
  });

  const cognitoSignup = async ({
    username,
    phoneNumber,
    password,
    extraUserProperties,
    onComplete,
    onFail,
  }: ICognitoSignupCardinals) => {
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username);
    const isPhoneNumber = !isEmpty(phoneNumber);
    const userAttributes = [];
    const usedUserName = isEmail || isPhoneNumber ? uuid() : username;
    if (isEmail) {
      userAttributes.push({ Name: 'email', Value: username });
    }
    if (isPhoneNumber) {
      userAttributes.push({ Name: 'phone_number', Value: phoneNumber });
    }

    forEach(extraUserProperties ?? {}, (value, key) => {
      userAttributes.push({ Name: snakeCase(key), Value: value });
    });

    const mixpanelTrackData = {
      username,
      phoneNumber,
      isEmail,
      ...extraUserProperties,
    };

    const command = new SignUpCommand({
      ClientId: clientId,
      Username: usedUserName,
      Password: password,
      UserAttributes: userAttributes,
    });

    const result = client.send(command);

    const mixpanelAction = 'cognito_signup';
    track(mixpanelAction, mixpanelTrackData);
    result
      .then(async (x) => {
        if (isEmail) {
          track('signup_mfa', {
            ...mixpanelTrackData,
            ...(x?.CodeDeliveryDetails ?? {}),
          });
          state.setChallenge({
            name: 'SMS_MFA',
            params: {
              CODE_DELIVERY_DELIVERY_MEDIUM:
                x.CodeDeliveryDetails?.DeliveryMedium === 'EMAIL' ? 'email' : 'sms',
              CODE_DELIVERY_DESTINATION: x.CodeDeliveryDetails?.Destination ?? '',
            },
            onFinish(code) {
              const confirmCommand = new ConfirmSignUpCommand({
                ClientId: clientId,
                Username: usedUserName,
                ConfirmationCode: code,
              });

              const mixpanelMfaAction = 'signup_sending_mfa_code';
              track(mixpanelMfaAction, mixpanelTrackData);

              client
                .send(confirmCommand)
                .then(async () => {
                  trackSuccess(mixpanelAction, mixpanelTrackData);
                  onComplete({
                    cognitoUsername: usedUserName!,
                    invite: inviteKey!,
                  });
                })
                .catch((err) => {
                  trackFail(mixpanelMfaAction, mixpanelTrackData);
                  signUpStore.setErrorMessager(err.message);
                  if (onFail) {
                    onFail(err.message);
                  }
                });
            },
            onResend() {
              track('resend_mfa_code', mixpanelTrackData);
              signUp({ username, password, phoneNumber });
            },
            onBack() {
              signUpStore.setErrorMessager('');
              state.resetChallenge();
            },
          });
        } else {
          trackSuccess(mixpanelAction, mixpanelTrackData);

          onComplete({
            cognitoUsername: usedUserName!,
            invite: inviteKey!,
          });
        }
      })
      .catch((err) => {
        trackFail(mixpanelAction, { ...mixpanelTrackData, err: err?.message });
        signUpStore.setErrorMessager(err.message);
        if (onFail) {
          onFail(err.message);
        }
      });
  };

  const signUp = async (data: ISignupCardinals) => {
    const mixpanelTrackData = {
      username: data.username,
      phoneNumber: data.phoneNumber,
    };

    track('starting_complete_signup', mixpanelTrackData);
    try {
      signUpStore.setIsLoading(true);
      await cognitoSignup({
        ...data,
        async onComplete(card) {
          trackSuccess('complete_signup_cognito_signup', mixpanelTrackData);
          await mutateSignup.mutateAsync(card);
          trackSuccess('user_created_in_app', mixpanelTrackData);
        },
        onFail(err) {
          trackFail('complete_signup_cognito_sign_up', {
            err,
            ...mixpanelTrackData,
          });
        },
      });
    } catch (err) {
      trackFail('signup_general_error', { err, ...mixpanelTrackData });
    } finally {
      signUpStore.setIsLoading(false);
    }
  };

  return {
    state: signUpStore,
    challenge: state.challenge,
    signUp,
    signUpWithToken: mutateTokenSignup.mutateAsync,
    cognitoSignup,
  };
};
